import { useEffect, useRef, useState } from 'react';
import { CATEGORY_LIST, PRODUCTS } from '../../data/catalog';

const FOUNDED = 2007;
const yearsInBusiness = Math.max(1, new Date().getFullYear() - FOUNDED);

type Stat = {
  /** Numeric target for the count-up; omit for a text-only stat. */
  value?: number;
  suffix?: string;
  text?: string;
  label: string;
};

const STATS: Stat[] = [
  { value: yearsInBusiness, suffix: '+', label: `Years of manufacturing, since ${FOUNDED}` },
  { value: 1800, suffix: '+', label: 'Shades tinted on demand' },
  { value: PRODUCTS.filter((p) => !p.hidden).length, label: `Products across ${CATEGORY_LIST.length} categories` },
  { text: 'Best Water-Based Paint Manufacturer', label: 'PaintVision Awards' },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && 'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/** Counts from 0 to `target` once `active` flips true. Jumps straight to the end under reduced motion. */
const useCountUp = (target: number, active: boolean, duration = 1600) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion() || typeof window === 'undefined' || !('requestAnimationFrame' in window)) {
      setN(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setN(Math.round(easeOutCubic(p) * target));
      if (p < 1) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return n;
};

const Figure = ({ stat, active }: { stat: Stat; active: boolean }) => {
  const n = useCountUp(stat.value ?? 0, active);
  if (stat.value === undefined) {
    return <span className="block text-xl sm:text-2xl lg:text-[1.65rem] font-bold text-brand-gold leading-tight">{stat.text}</span>;
  }
  return (
    <span className="block text-4xl sm:text-5xl font-bold text-brand-gold leading-none tabular-nums">
      {n.toLocaleString('en-IN')}
      {stat.suffix}
    </span>
  );
};

/** Navy band with four count-up figures; animation starts when scrolled into view. */
const StatsBand = () => {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setActive(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActive(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden bg-brand-navy text-white">
      <div className="pointer-events-none absolute -top-32 left-1/4 w-[30rem] h-[30rem] rounded-full bg-brand-blue/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 right-0 w-[24rem] h-[24rem] rounded-full bg-brand-gold/10 blur-3xl" />
      <div className="relative container py-14 lg:py-16">
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 lg:gap-x-10">
          {STATS.map((s, i) => (
            <div key={s.label} className={`flex flex-col ${i > 0 ? 'lg:border-l lg:border-white/10 lg:pl-10' : ''}`}>
              {/* dt precedes dd in the markup; flex order puts the figure on top visually. */}
              <dt className="order-2 mt-3 text-sm text-white/65 leading-snug max-w-[14rem]">{s.label}</dt>
              <dd className="order-1">
                <Figure stat={s} active={active} />
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default StatsBand;
